import React, { useRef, useState } from "react";
import { CloudUploadOutlined } from "@ant-design/icons";
import axios from "axios";
import { openNotification } from "../../../../Components/Notification";
import Dragger from "antd/es/upload/Dragger";
import bulkUploadService from "../../../../Service/bulk.upload.service";
import { useAILoader } from "../../../../Context/AILoaderContext";
import { Job } from "../../../../Entities/Job";
import { message, Alert } from "antd";
import { classifyError } from "../../../../Utils/errorClassifier";
import { ERROR_MESSAGES } from "../../../../Utils/errorMessages";

interface FileUploadDraggerProps {
  setShowUploadModal: (show: boolean) => void;
  jobDescriptionId: string;
  handleNewJobAdd: (job: Job | null) => void;
}

const FileUploadDragger: React.FC<FileUploadDraggerProps> = ({
  jobDescriptionId,
  setShowUploadModal,
  handleNewJobAdd,
}) => {
  const { setAILoaderMessage, setIsAILoaderLoading, isAILoaderLoading } =
    useAILoader();
  const [fileList, setFileList] = useState<File[]>([]);
  const [uploadError, setUploadError] = useState<any>(null);

  const count = useRef(0);
  const MAX_FILES = 5;
  const MAX_FILE_SIZE = 5 * 1024 * 1024;

  const handleClose = () => {
    setShowUploadModal(false);
    setUploadError(null);
  };

  const getPresignedUrl = async (
    fileName: string,
    fileType: string,
    JobDescriptionId: string,
  ) => {
    try {
      const data = await bulkUploadService.getUploadPresignedUrl(
        fileName,
        fileType,
        JobDescriptionId,
      );
      return data;
    } catch (error) {
      console.error("🚀 ~ error:", error);
      throw error;
    }
  };

  const startJobProcess = async (
    fileName: string,
    JobDescriptionId: string,
  ) => {
    try {
      const data =
        await bulkUploadService.startJobCreatingProcessForSingleUpload(
          JobDescriptionId,
          fileName,
        );
      return data;
    } catch (error) {
      console.error("🚀 ~ error:", error);
      throw error;
    }
  };

  const uploadFile = async (file: File) => {
    try {
      console.log(`Uploading file: ${file.name}`);
      console.log("File list", fileList);
      setUploadError(null);

      if (
        ![
          "application/pdf",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        ].includes(file.type)
      ) {
        openNotification("error", "Only PDF and DOCX files are allowed", "");
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        openNotification("error", "File size cannot exceed 5MB", "");
        return;
      }

      setIsAILoaderLoading(true);
      setAILoaderMessage("Uploading file...");

      const { url, fileName } = await getPresignedUrl(
        file.name,
        file.type,
        jobDescriptionId,
      );

      await axios.put(url, file, {
        headers: {
          "Content-Type": file.type,
        },
      });

      setAILoaderMessage("Processing, Please wait...");

      const processData = await startJobProcess(fileName, jobDescriptionId);
      console.log("Process data", processData);
      if (processData?.status) {
        message.success("File uploaded and processed successfully");
      } else {
        // Soft-error: classify and show structured error
        const classified = classifyError(processData);
        const msgs = ERROR_MESSAGES[classified.category];
        setUploadError({
          headline: msgs.recruiterHeadline,
          body: msgs.recruiterBody,
          guidance: msgs.recruiterGuidance,
          isRetryable: classified.isRetryable,
        });
      }
      handleNewJobAdd(processData?.job || null);
      handleClose();
    } catch (error: any) {
      console.error("File upload error:", error);
      const classified = classifyError(error);
      const msgs = ERROR_MESSAGES[classified.category];
      setUploadError({
        headline: msgs.recruiterHeadline,
        body: msgs.recruiterBody,
        guidance: msgs.recruiterGuidance,
        isRetryable: classified.isRetryable,
      });
    } finally {
      setIsAILoaderLoading(false);
      setAILoaderMessage(null);
    }
  };

  const beforeUpload = async (file: File) => {
    try {
      count.current = count.current + 1;

      if (count.current > MAX_FILES) {
        message.error(`Cannot upload more than ${MAX_FILES} files`);
        return false;
      }

      if (
        ![
          "application/pdf",
          "application/msword",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        ].includes(file.type)
      ) {
        message.error("Only PDF, DOC and DOCX files are allowed");
        return false;
      }

      if (file.size > MAX_FILE_SIZE) {
        message.error("File size cannot exceed 5MB");
        return false; // Prevent further processing
      }

      await uploadFile(file); // Proceed to upload
      setFileList([...fileList, file]); // Update the file list only on successful upload
    } catch (error) {
      message.error("An error occurred during upload. Please try again.");
      console.error("Error in beforeUpload:", error);
    }
    return false; // Prevent automatic upload
  };

  const handleFileChange = (info: any) => {
    const selectedFiles = info.fileList;
    console.log("selectedFiles", selectedFiles);

    if (selectedFiles?.length > MAX_FILES) {
      message.error(`You can select a maximum of ${MAX_FILES} files`);
    }

    setFileList(selectedFiles.slice(0, MAX_FILES));
  };

  const props = {
    name: "file",
    multiple: true,
    accept: ".pdf,.doc,.docx",
    beforeUpload,
    maxCount: MAX_FILES,
    onChange: handleFileChange,
    onDrop: (e: React.DragEvent<HTMLDivElement>) => {
      console.debug("Dropped files", e.dataTransfer.files);
    },
  };

  return (
    <div>
      {uploadError && (
        <Alert
          type="error"
          showIcon
          message={uploadError.headline}
          description={
            <div>
              <p style={{ margin: "8px 0 0 0" }}>{uploadError.body}</p>
              {uploadError.guidance && uploadError.guidance.length > 0 && (
                <ul
                  style={{
                    margin: "8px 0 0 0",
                    paddingLeft: "20px",
                    fontSize: "13px",
                  }}
                >
                  {uploadError.guidance.map((item: string, idx: number) => (
                    <li key={idx} style={{ marginBottom: "4px" }}>
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          }
          style={{ marginBottom: "16px" }}
        />
      )}
      <Dragger {...props} disabled={isAILoaderLoading}>
        <p className="ant-upload-drag-icon">
          <CloudUploadOutlined />
        </p>
        <p className="ant-upload-text">
          {isAILoaderLoading
            ? "Uploading files and processing..."
            : "Click or drag PDF, DOC or DOCX files to this area to upload"}
          <p>(Maximum of 5 files are allowed)</p>
        </p>
      </Dragger>
    </div>
  );
};

export default FileUploadDragger;
